import { useState, useEffect, useMemo } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Search,
  Package,
  Plus,
  Minus,
  Trash2,
  ShoppingCart,
  FileText,
  AlertTriangle,
  Check,
  X,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = {
  screen: "Écran",
  battery: "Batterie",
  camera: "Caméra",
  connector: "Connecteur",
  speaker: "Haut-parleur",
  flex: "Nappe/Flex",
  button: "Bouton",
  housing: "Châssis/Vitre",
  accessory: "Accessoire",
};

export default function PartsSelectionDialog({
  open,
  onOpenChange,
  repairId,
  repair,
  onPartsAdded,
  onCreateQuote,
}) {
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [brandFilter, setBrandFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [selectedParts, setSelectedParts] = useState([]);
  const [updating, setUpdating] = useState(false);

  // Fetch parts
  useEffect(() => {
    if (open) {
      fetchParts();
    }
  }, [open]);

  const fetchParts = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/api/parts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setParts(res.data);
    } catch (err) {
      toast.error("Erreur lors du chargement des pièces");
    } finally {
      setLoading(false);
    }
  };

  // Get unique brands
  const brands = useMemo(() => {
    const uniqueBrands = [...new Set(parts.map((p) => p.brand || "Universel"))];
    return uniqueBrands.sort();
  }, [parts]);

  // Filter parts
  const filteredParts = useMemo(() => {
    return parts.filter((p) => {
      const matchesSearch =
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (p.reference && p.reference.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (p.model && p.model.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesBrand = brandFilter === "all" || p.brand === brandFilter;
      const matchesCategory = categoryFilter === "all" || p.category === categoryFilter;
      return matchesSearch && matchesBrand && matchesCategory;
    });
  }, [parts, searchQuery, brandFilter, categoryFilter]);

  // Group parts by brand and model
  const groupedParts = useMemo(() => {
    const groups = {};
    filteredParts.forEach((part) => {
      const brand = part.brand || "Universel";
      if (!groups[brand]) {
        groups[brand] = [];
      }
      groups[brand].push(part);
    });
    // Sort parts within each brand by model then by category
    Object.keys(groups).forEach((brand) => {
      groups[brand].sort((a, b) => {
        if (a.model !== b.model) return (a.model || "").localeCompare(b.model || "");
        return (a.category || "").localeCompare(b.category || "");
      });
    });
    return groups;
  }, [filteredParts]);

  const togglePartSelection = (part) => {
    const existing = selectedParts.find((p) => p.part_id === part.id);
    if (existing) {
      setSelectedParts(selectedParts.filter((p) => p.part_id !== part.id));
    } else {
      setSelectedParts([
        ...selectedParts,
        {
          part_id: part.id,
          part_name: part.name,
          quantity: 1,
          unit_price: part.unit_price,
          available_qty: part.quantity,
        },
      ]);
    }
  };

  const updatePartQuantity = (partId, newQty) => {
    if (newQty < 1) return;
    setSelectedParts(
      selectedParts.map((p) =>
        p.part_id === partId ? { ...p, quantity: newQty } : p
      )
    );
  };

  const isPartSelected = (partId) => selectedParts.some((p) => p.part_id === partId);

  const handleAddParts = async (createQuote = false) => {
    if (selectedParts.length === 0) {
      toast.error("Veuillez sélectionner au moins une pièce");
      return;
    }

    setUpdating(true);
    try {
      const token = localStorage.getItem("token");
      const partsToAdd = selectedParts.map((p) => ({
        part_id: p.part_id,
        quantity: p.quantity,
      }));

      const response = await axios.post(
        `${API_URL}/api/repairs/${repairId}/add-parts-smart`,
        partsToAdd,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { parts_added, parts_ordered, parts_pending_quote, quote_accepted, message } = response.data;

      if (!quote_accepted && parts_pending_quote && parts_pending_quote.length > 0) {
        const needsOrder = parts_pending_quote.filter((p) => p.needs_order);
        if (needsOrder.length > 0) {
          toast.info(
            `${parts_pending_quote.length} pièce(s) enregistrée(s). ${needsOrder.length} devront être commandée(s) après acceptation du devis.`,
            { duration: 5000 }
          );
        } else {
          toast.success(`${parts_pending_quote.length} pièce(s) enregistrée(s).`);
        }
      } else {
        if (parts_ordered && parts_ordered.length > 0) {
          toast.warning(`${parts_ordered.length} pièce(s) en rupture - ajoutée(s) aux commandes`);
        }
        if (parts_added && parts_added.length > 0) {
          toast.success(`${parts_added.length} pièce(s) ajoutée(s) depuis le stock`);
        }
      }

      if (createQuote && onCreateQuote) {
        // Create quote with selected parts
        onCreateQuote(selectedParts);
      }

      onOpenChange(false);
      setSelectedParts([]);
      if (onPartsAdded) onPartsAdded();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setUpdating(false);
    }
  };

  // Check if quote is accepted
  const quoteAccepted = repair?.status === "quote_accepted" || repair?.status === "pending_parts" || repair?.status === "in_repair";

  // Calculate totals
  const totalParts = selectedParts.reduce((sum, p) => sum + p.quantity, 0);
  const totalPrice = selectedParts.reduce((sum, p) => sum + p.quantity * p.unit_price, 0);
  const partsNeedingOrder = selectedParts.filter((p) => p.available_qty < p.quantity);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-chivo flex items-center gap-2">
            <Package className="w-5 h-5 text-primary" />
            Sélection des pièces
          </DialogTitle>
          {!quoteAccepted && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-md p-3 mt-2">
              <p className="text-sm text-amber-400 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Devis non accepté. Les pièces seront enregistrées et les commandes créées après acceptation.
              </p>
            </div>
          )}
        </DialogHeader>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 py-3 border-b border-border">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher une pièce..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-secondary/50"
            />
          </div>
          <Select value={brandFilter} onValueChange={setBrandFilter}>
            <SelectTrigger className="w-40 bg-secondary/50">
              <SelectValue placeholder="Marque" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes marques</SelectItem>
              {brands.map((brand) => (
                <SelectItem key={brand} value={brand}>
                  {brand}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-40 bg-secondary/50">
              <SelectValue placeholder="Catégorie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes catégories</SelectItem>
              {Object.entries(CATEGORIES).map(([key, label]) => (
                <SelectItem key={key} value={key}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Parts List */}
        <div className="flex-1 overflow-y-auto py-2 min-h-0">
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Chargement...</div>
          ) : filteredParts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Package className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>Aucune pièce trouvée</p>
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(groupedParts).map(([brand, brandParts]) => (
                <div key={brand}>
                  <h3 className="font-semibold text-primary mb-2 sticky top-0 bg-card py-1">
                    {brand}
                  </h3>
                  <div className="space-y-1">
                    {brandParts.map((part) => {
                      const isSelected = isPartSelected(part.id);
                      const selectedPart = selectedParts.find((p) => p.part_id === part.id);
                      const needsOrder = isSelected && selectedPart && selectedPart.quantity > part.quantity;

                      return (
                        <div
                          key={part.id}
                          className={`flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors ${
                            isSelected
                              ? "bg-primary/10 border border-primary/30"
                              : "hover:bg-secondary/50"
                          }`}
                          onClick={() => togglePartSelection(part)}
                        >
                          <Checkbox
                            checked={isSelected}
                            className="pointer-events-none"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{part.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {part.model !== "Universel" && `${part.model} • `}
                              {CATEGORIES[part.category] || part.category}
                              {part.reference && ` • ${part.reference}`}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className={`text-xs ${part.quantity > 0 ? "text-emerald-500" : "text-amber-500"}`}>
                              {part.quantity > 0 ? `${part.quantity} en stock` : "Rupture"}
                            </p>
                            <p className="text-sm font-medium">{part.unit_price.toFixed(2)}€</p>
                          </div>
                          {isSelected && (
                            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7"
                                onClick={() => updatePartQuantity(part.id, (selectedPart?.quantity || 1) - 1)}
                              >
                                <Minus className="w-3 h-3" />
                              </Button>
                              <span className="w-8 text-center text-sm">{selectedPart?.quantity || 1}</span>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7"
                                onClick={() => updatePartQuantity(part.id, (selectedPart?.quantity || 1) + 1)}
                              >
                                <Plus className="w-3 h-3" />
                              </Button>
                            </div>
                          )}
                          {needsOrder && (
                            <span className="text-xs px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded">
                              À commander
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Selected Parts Summary */}
        {selectedParts.length > 0 && (
          <div className="border-t border-border pt-3 space-y-3">
            <div className="flex flex-wrap gap-2">
              {selectedParts.map((part) => (
                <div
                  key={part.part_id}
                  className="flex items-center gap-2 bg-secondary/50 px-2 py-1 rounded-md text-sm"
                >
                  <span>{part.part_name} (×{part.quantity})</span>
                  <button
                    onClick={() => setSelectedParts(selectedParts.filter((p) => p.part_id !== part.part_id))}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {totalParts} pièce(s) sélectionnée(s)
                {partsNeedingOrder.length > 0 && (
                  <span className="text-amber-500 ml-2">
                    ({partsNeedingOrder.length} à commander)
                  </span>
                )}
              </span>
              <span className="font-semibold">Total: {totalPrice.toFixed(2)}€</span>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-2 pt-3 border-t border-border">
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              setSelectedParts([]);
            }}
            className="flex-1 sm:flex-none"
          >
            Annuler
          </Button>
          <div className="flex-1" />
          <Button
            variant="outline"
            onClick={() => handleAddParts(false)}
            disabled={selectedParts.length === 0 || updating}
            className="flex-1 sm:flex-none"
          >
            <Check className="w-4 h-4 mr-2" />
            {updating ? "Enregistrement..." : "Enregistrer les pièces"}
          </Button>
          <Button
            onClick={() => handleAddParts(true)}
            disabled={selectedParts.length === 0 || updating}
            className="flex-1 sm:flex-none bg-primary hover:bg-primary/90"
          >
            <FileText className="w-4 h-4 mr-2" />
            Enregistrer et créer devis
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
